import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  GaugeIcon,
  CarIcon,
  ClockCounterClockwiseIcon,
  FileArrowDownIcon,
  GearSixIcon,
  UsersThreeIcon,
  SignOutIcon,
  ListIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LanguageToggle } from "@/components/LanguageToggle";

const NavItem = ({ to, icon: Icon, label, testid, onNavigate }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    onClick={onNavigate}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 border-s-2 transition-colors text-sm ${
        isActive
          ? "border-[#007AFF] bg-[#1C1C1C] text-white"
          : "border-transparent text-zinc-400 hover:text-white hover:bg-[#121212]"
      }`
    }
  >
    <Icon size={18} weight="bold" />
    <span className="tracking-wide">{label}</span>
  </NavLink>
);

const SidebarContent = ({ t, user, handleLogout, onNavigate }) => (
  <>
    <div className="px-6 py-6 border-b border-white/10">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Fleet</div>
      {user?.office_logo ? (
        <div className="mt-2 flex items-center gap-3">
          <img
            src={user.office_logo}
            alt="logo"
            className="w-10 h-10 object-cover border border-white/10"
            data-testid="sidebar-office-logo"
          />
          <div className="min-w-0">
            <div className="text-base font-bold leading-tight truncate" data-testid="sidebar-office-name">
              {user.office_name || t("appName")}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-2xl font-bold mt-1 leading-none truncate" data-testid="sidebar-office-name">
          {user?.office_name || t("appName")}
        </div>
      )}
    </div>
    <nav className="flex-1 py-4 overflow-y-auto">
      <NavItem to="/dashboard" icon={GaugeIcon} label={t("dashboard")} testid="nav-dashboard" onNavigate={onNavigate} />
      <NavItem to="/cars" icon={CarIcon} label={t("cars")} testid="nav-cars" onNavigate={onNavigate} />
      <NavItem to="/trips" icon={ClockCounterClockwiseIcon} label={t("trips")} testid="nav-trips" onNavigate={onNavigate} />
      <NavItem to="/reports" icon={FileArrowDownIcon} label={t("reports")} testid="nav-reports" onNavigate={onNavigate} />
      {user?.role === "admin" && (
        <>
          <NavItem to="/admin/users" icon={UsersThreeIcon} label={t("accounts")} testid="nav-users" onNavigate={onNavigate} />
          <NavItem to="/admin/site" icon={GaugeIcon} label={"إدارة الموقع"} testid="nav-site" onNavigate={onNavigate} />
        </>
      )}
      <NavItem to="/settings" icon={GearSixIcon} label={t("settingsPage")} testid="nav-settings" onNavigate={onNavigate} />
    </nav>
    <div className="border-t border-white/10 px-4 py-4 space-y-3">
      {user && (
        <div className="text-xs text-zinc-500">
          <div className="uppercase tracking-[0.2em] mb-1">{t("signedInAs")}</div>
          <div className="text-zinc-200 truncate" data-testid="sidebar-user-email">
            {user.role === "admin" ? "ADMIN · " : ""}{user.username}
          </div>
          {user.expires_at && (
            <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-[0.2em]">
              {t("expires")}: {new Date(user.expires_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )}
      <button
        onClick={handleLogout}
        data-testid="logout-btn"
        className="w-full inline-flex items-center gap-2 border border-white/10 bg-[#121212] hover:bg-[#1C1C1C] px-3 py-2 text-xs uppercase tracking-[0.2em] text-zinc-300 transition-colors"
      >
        <SignOutIcon size={14} weight="bold" />
        {t("logout")}
      </button>
    </div>
  </>
);

export const AppShell = ({ children }) => {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer when route changes
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  const drawerSide = lang === "ar" ? "right-0" : "left-0";
  const drawerHidden = lang === "ar" ? "translate-x-full" : "-translate-x-full";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-shrink-0 min-h-screen border-e border-white/10 bg-[#0A0A0A] flex-col">
        <SidebarContent t={t} user={user} handleLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-drawer-backdrop"
        />
      )}
      <aside
        className={`lg:hidden fixed top-0 ${drawerSide} bottom-0 z-50 w-72 max-w-[85vw] bg-[#0A0A0A] border-e border-white/10 flex flex-col transform transition-transform duration-200 ease-out ${
          mobileOpen ? "translate-x-0" : drawerHidden
        }`}
        data-testid="mobile-drawer"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Menu</div>
          <button
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-close"
            className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
            aria-label="close"
          >
            <XIcon size={16} weight="bold" />
          </button>
        </div>
        <SidebarContent t={t} user={user} handleLogout={handleLogout} onNavigate={() => setMobileOpen(false)} />
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 w-full">
        <div className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10 px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 border border-white/10 bg-[#121212] hover:bg-[#1C1C1C]"
              aria-label="menu"
            >
              <ListIcon size={18} weight="bold" />
            </button>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 truncate hidden sm:block">
              {t("appName")} · {t("tagline")}
            </div>
            <div className="text-sm font-bold sm:hidden truncate">{t("appName")}</div>
          </div>
          <LanguageToggle />
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</div>
        <footer className="border-t border-white/10 px-4 sm:px-8 py-5 mt-8 text-center" data-testid="app-footer">
          <div className="text-xs sm:text-sm text-zinc-500 tracking-wide">
            © {new Date().getFullYear()} · {t("footerRights")}
          </div>
        </footer>
      </main>
    </div>
  );
};
